import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { computed, effect, signal } from '@preact/signals-core';
import { css } from 'lit';
import { property } from 'lit/decorators.js';

import {
  renderUniLit,
  trackSelectedAttachmentCell,
} from '../../../core/index.js';
import type {
  CellRenderProps,
  DataViewCellLifeCycle,
} from '../../../core/property/index.js';
import { DATA_VIEW_CELL_PRESENCE_EVENT } from '../../../core/row-focus.js';
import {
  dataViewCellContainerStyles,
  shouldExpandDataViewCell,
} from '../../../core/view/cell-container.js';
import {
  TableViewAreaSelection,
  type TableViewSelectionWithType,
} from '../selection';
import type { TableProperty, TableSingleView } from '../table-view-manager.js';
import type { TableGroup } from './group.js';
import type { TableViewUILogic } from './table-view-ui-logic.js';
export class TableViewCellContainer extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    ${dataViewCellContainerStyles('dv-table-view-cell-container')}
  `;

  private readonly _cell$ = signal<DataViewCellLifeCycle>();

  @property({ attribute: false })
  accessor column!: TableProperty;

  @property({ attribute: false })
  accessor rowId!: string;

  cell$ = computed(() => {
    return this.column.cellGetOrCreate(this.rowId);
  });

  selectCurrentCell = (editing: boolean) => {
    if (this.view.readonly$.value) {
      return;
    }
    const selectionView = this.selectionController;
    if (selectionView) {
      const selection = selectionView.selection;
      if (selection && this.isSelected(selection) && editing) {
        selectionView.selection = TableViewAreaSelection.create({
          groupKey: this.groupKey,
          focus: {
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
          },
          isEditing: true,
        });
      } else {
        selectionView.selection = TableViewAreaSelection.create({
          groupKey: this.groupKey,
          focus: {
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
          },
          isEditing: false,
        });
      }
    }
  };

  get cell(): DataViewCellLifeCycle | undefined {
    return this._cell$.value;
  }

  private get groupKey() {
    return this.closest<TableGroup>('affine-data-view-table-group')?.group?.key;
  }

  private get selectionController() {
    return this.tableViewLogic.selectionController;
  }

  private readonly _stopKeyboardPropagationInEditing = (
    event: KeyboardEvent
  ) => {
    if (this.isEditing$.value && event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  private publishCellPresenceTarget() {
    this.dispatchEvent(
      new CustomEvent(DATA_VIEW_CELL_PRESENCE_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          columnId: this.columnId,
          mode: this.isEditing$.value ? 'editing' : 'selected',
          rowId: this.rowId,
          viewId: this.view.id,
        },
      })
    );
  }

  override connectedCallback() {
    super.connectedCallback();
    this._disposables.addFromEvent(this, 'click', () => {
      if (!this.isEditing$.value) {
        this.selectCurrentCell(!this.column.valueReadonly$.value);
      }
    });
    this._disposables.addFromEvent(
      this,
      'keydown',
      this._stopKeyboardPropagationInEditing
    );
    this._disposables.addFromEvent(
      this,
      'keyup',
      this._stopKeyboardPropagationInEditing
    );

    // Dblclick always enters edit mode — the 2-click "select then
    // click again" path is defeated in the fullscreen table by the
    // row-level click handler (row.ts `_handleRowClick` flips the
    // selection to row type in capture phase, so the cell's second
    // click sees a non-matching selection and only re-selects). A
    // dedicated dblclick shortcut bypasses that interaction and
    // matches the gantt swimlane label's dblclick semantics, so both
    // views feel identical for select / multi-select editing.
    this._disposables.addFromEvent(this, 'dblclick', e => {
      if (this.isEditing$.value) return;
      if (this.view.readonly$.value) return;
      if (this.column.valueReadonly$.value) return;
      const sel = this.selectionController;
      if (!sel) return;
      e.stopPropagation();
      sel.selection = TableViewAreaSelection.create({
        groupKey: this.groupKey,
        focus: {
          rowIndex: this.rowIndex,
          columnIndex: this.columnIndex,
        },
        isEditing: true,
      });
    });

    // Effect for cell display mode (expanded, collapsed, or smart)
    this._disposables.add(
      effect(() => {
        const tableView = this.view as TableSingleView;
        const mode = tableView.cellDisplayMode$.value;

        const selection = this.tableViewLogic.selection$.value;
        const expanded = shouldExpandDataViewCell({
          isEditing: this.isEditing$.value,
          mode,
          rowSelected: this.isRowSelected(selection),
        });
        this.dataset['expanded'] = `${expanded}`;
      })
    );

    this._disposables.add(
      effect(() => {
        const selection = this.tableViewLogic.selection$.value;
        this.isEditing$.value;
        const selected = !!selection && !!this.isSelected(selection);
        this.dataset['databaseCellSelected'] = `${selected}`;
        trackSelectedAttachmentCell(this, {
          columnType: this.column.type$.value,
          selected,
        });
        if (!selected) {
          return;
        }
        this.publishCellPresenceTarget();
      })
    );
  }

  /**
   * Check if this row is currently selected (for smart expand mode)
   */
  private isRowSelected(
    selection: TableViewSelectionWithType | undefined
  ): boolean {
    if (!selection) return false;

    if (selection.selectionType === 'area') {
      // Area selection: check if focus row matches
      if (selection.groupKey !== this.groupKey) return false;
      return selection.focus.rowIndex === this.rowIndex;
    } else if (selection.selectionType === 'row') {
      // Row selection: check if this row is in the selected rows
      return selection.rows.some(
        row => row.id === this.rowId && row.groupKey === this.groupKey
      );
    }
    return false;
  }

  isSelected(selection: TableViewSelectionWithType) {
    if (selection.selectionType !== 'area') {
      return false;
    }
    if (selection.groupKey !== this.groupKey) {
      return;
    }
    if (selection.focus.columnIndex !== this.columnIndex) {
      return;
    }
    return selection.focus.rowIndex === this.rowIndex;
  }

  override render() {
    const renderer = this.column.renderer$.value;
    if (!renderer) {
      return;
    }
    const { view } = renderer;
    this.view.lockRows(this.isEditing$.value);
    this.dataset['editing'] = `${this.isEditing$.value}`;
    this.dataset['databaseCellEditing'] = `${this.isEditing$.value}`;
    this.dataset['rowId'] = this.rowId;
    this.dataset['columnId'] = this.columnId;
    this.dataset['columnType'] = this.column.type$.value;
    const props: CellRenderProps = {
      cell: this.cell$.value,
      isEditing$: this.isEditing$,
      selectCurrentCell: this.selectCurrentCell,
    };

    return renderUniLit(view, props, {
      ref: this._cell$,
      style: {
        display: 'contents',
      },
    });
  }

  @property({ attribute: false })
  accessor columnId!: string;

  @property({ attribute: false })
  accessor columnIndex!: number;

  isEditing$ = signal(false);

  @property({ attribute: false })
  accessor rowIndex!: number;

  get view() {
    return this.tableViewLogic.view;
  }

  @property({ attribute: false })
  accessor tableViewLogic!: TableViewUILogic;
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-table-view-cell-container': TableViewCellContainer;
  }
}
